import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';

/**
 * Dialog that displays theoretical calculations and limitations for a requirement
 */
const CalculationsDialog = ({ open, onClose, data = null, loading = false, error = null }) => {
  // Helper to check if we have valid calculations
  const hasCalculations = data && data.calculations && data.calculations.length > 0;
  const canCalculate = data && data.can_calculate;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      scroll="paper"
      aria-labelledby="calculations-dialog-title"
    >
      <DialogTitle id="calculations-dialog-title">
        Theoretical Calculations & Limitations
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {typeof error === 'string' ? error : 'Error loading calculations'}
          </Alert>
        ) : !canCalculate ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {data?.message || 'No calculations could be performed for this requirement.'}
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Message section */}
            {data?.message && (
              <Typography variant="body1">
                {data.message}
              </Typography>
            )}
            
            {/* Calculations table */}
            {hasCalculations && (
              <TableContainer component={Paper} variant="outlined">
                <Table aria-label="calculations table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Parameter</TableCell>
                      <TableCell>Minimum</TableCell>
                      <TableCell>Maximum</TableCell>
                      <TableCell>Units</TableCell>
                      <TableCell>Formula</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.calculations.map((calc) => (
                      <TableRow key={calc.parameter} hover>
                        <TableCell component="th" scope="row">
                          {calc.parameter}
                        </TableCell>
                        <TableCell>{calc.minimum}</TableCell>
                        <TableCell>{calc.maximum}</TableCell>
                        <TableCell>{calc.units}</TableCell>
                        <TableCell><code>{calc.formula}</code></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Reasoning section */}
            {hasCalculations && data.calculations.some(calc => calc.reasoning) && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Reasoning
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {data.calculations.map((calc) => (
                  <Box key={calc.parameter} sx={{ mb: 2 }}>
                    {calc.reasoning && (
                      <>
                        <Typography variant="subtitle1">{calc.parameter}</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                          {calc.reasoning}
                        </Typography>
                      </>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {/* #793: no `autoFocus` — MUI's Dialog already moves focus into itself on
            open, and Close is the only action here, so nothing extra is needed. */}
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CalculationsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
};

export default CalculationsDialog;